import { useEffect, useId, useState, type ReactNode } from 'react'
import { AlertCircle, ChevronDown, ChevronRight, Info } from 'lucide-react'
import { useSqlStorageRecommendation } from '@/hooks/useInfra'
import type { InfraProduct, SqlDiskGroup, SqlDiskSku, SqlDiskType, SqlStorageConfig } from '@/types/infrastructure'
import {
  SECTION_TITLES,
  defaultSqlStorage,
  describeDisks,
  describeGroup,
  pickSku,
  sectionHasProblem,
  skusOfType,
  sqlStorageProblems,
  storageNotices,
  validateSqlStorage,
  type SectionProblems,
  type StorageSection,
} from '@/utils/sqlStorage'
import { Button } from '@/components/common/Button'
import page from '@/pages/NewDeploymentPage.module.css'
import styles from './Infrastructure.module.css'
import { ReadOnlyInfoCard } from './ReadOnlyInfoCard'
import { TextField } from './TextField'

const DISK_TYPES: SqlDiskType[] = ['Premium SSD', 'Standard SSD']

const DESCRIPTIONS: Record<StorageSection, string> = {
  data: 'These managed disks will serve as the storage for your SQL Server database files. They will be configured as data disks on your virtual machine in Azure Storage.',
  log: 'These managed disks will serve as the storage for your SQL Server transaction log files. They will be configured as data disks on your virtual machine in Azure Storage.',
  tempDb: 'These managed disks will serve as the storage for the SQL Server tempdb database. They will be configured as data disks on your virtual machine in Azure Storage.',
}

const LOCATION_LABELS: Record<StorageSection, string> = { data: 'Data drive location', log: 'Log drive location', tempDb: 'TempDB drive location' }
const LOCATION_EXAMPLES: Record<StorageSection, string> = { data: 'F:\\data', log: 'G:\\log', tempDb: 'H:\\tempDb' }

interface DiskGroupFieldsProps {
  idPrefix: string
  title: string
  group: SqlDiskGroup
  skus: SqlDiskSku[]
  maxDisks: number
  problems: SectionProblems
  locationLabel: string
  locationExample: string
  onChange: (group: SqlDiskGroup) => void
}

/** The fields of one group of disks: drive location, disk type, the size table with the number of disks, and what it adds up to. */
function DiskGroupFields({ idPrefix, title, group, skus, maxDisks, problems, locationLabel, locationExample, onChange }: DiskGroupFieldsProps) {
  const options = skusOfType(skus, group.sku.diskType)
  if (!options.some((option) => option.id === group.sku.id)) options.push(group.sku)

  const changeType = (diskType: SqlDiskType) => {
    const sku = pickSku(skus, diskType, group.sku.sizeGb)
    if (sku) onChange({ ...group, sku })
  }
  const changeSku = (id: string) => {
    const sku = skus.find((item) => item.id === id)
    if (sku) onChange({ ...group, sku })
  }

  return (
    <>
      <div className={page.formGrid}>
        <TextField
          id={`${idPrefix}-location`}
          label={locationLabel}
          required
          value={group.driveLocation}
          onChange={(driveLocation) => onChange({ ...group, driveLocation })}
          problem={problems.location}
          hint={`Where the disks are mounted, for example ${locationExample}.`}
        />
        <div className={page.formGroup}>
          <label htmlFor={`${idPrefix}-type`} className={page.label}>
            Disk type <span className={page.required}>*</span>
          </label>
          <select id={`${idPrefix}-type`} className={page.select} value={group.sku.diskType} onChange={(event) => changeType(event.target.value as SqlDiskType)}>
            {DISK_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.diskTable}>
          <caption className={styles.srOnly}>{title} disks</caption>
          <thead>
            <tr>
              <th scope="col">Disk type</th>
              <th scope="col">Size (GiB)</th>
              <th scope="col">Max IOPS</th>
              <th scope="col">Max Throughput</th>
              <th scope="col">Number of disks</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <select
                  id={`${idPrefix}-size`}
                  className={page.select}
                  aria-label={`${title} disk size`}
                  value={group.sku.id}
                  onChange={(event) => changeSku(event.target.value)}
                >
                  {options.map((option) => (
                    <option key={option.id} value={option.id}>{`${option.sizeGb} GiB, ${option.diskType}`}</option>
                  ))}
                </select>
              </td>
              <td data-testid={`${idPrefix}-size-gib`}>{group.sku.sizeGb}</td>
              <td data-testid={`${idPrefix}-iops`}>{group.sku.maxIops}</td>
              <td data-testid={`${idPrefix}-throughput`}>{group.sku.maxThroughputMbps}</td>
              <td>
                <input
                  id={`${idPrefix}-count`}
                  className={page.input}
                  type="number"
                  min={1}
                  max={maxDisks}
                  aria-label={`${title} number of disks`}
                  aria-invalid={problems.count ? true : undefined}
                  aria-describedby={problems.count ? `${idPrefix}-count-problem` : undefined}
                  value={Number.isNaN(group.count) ? '' : group.count}
                  onChange={(event) => onChange({ ...group, count: Number.parseInt(event.target.value, 10) })}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {problems.count && (
        <span id={`${idPrefix}-count-problem`} role="alert" className={page.errorMsg}>
          {problems.count}
        </span>
      )}
      <p className={styles.diskInfo}>
        <Info size={14} aria-hidden="true" />
        <span data-testid={`${idPrefix}-summary`}>{describeDisks(group)}</span>
      </p>
    </>
  )
}

interface DisclosureProps {
  id: string
  title: string
  expanded: boolean
  /** A section with a problem is kept open, so the problem cannot be missed. */
  locked: boolean
  needsAttention: boolean
  onToggle: () => void
  children: ReactNode
}

function Disclosure({ id, title, expanded, locked, needsAttention, onToggle, children }: DisclosureProps) {
  const open = expanded || locked
  return (
    <div className={styles.storageSection}>
      <button
        type="button"
        className={styles.storageSectionHeader}
        aria-expanded={open}
        aria-controls={`${id}-body`}
        aria-disabled={locked || undefined}
        title={locked ? 'Fix the problems in this section first.' : undefined}
        onClick={() => {
          if (!locked) onToggle()
        }}
      >
        {open ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
        {title}
        {needsAttention && <span className={styles.attention}>Needs attention</span>}
      </button>
      {open && (
        <div id={`${id}-body`} className={styles.storageSectionBody}>
          {children}
        </div>
      )}
    </div>
  )
}

interface SqlStorageEditorProps {
  product: InfraProduct
  regionId: string
  /** The saved configuration. Null until the recommendation has loaded: the recommended default is then applied. */
  value: SqlStorageConfig | null
  onChange: (config: SqlStorageConfig) => void
  idPrefix: string
}

/**
 * The SQL server's disks, laid out like the Azure "Configure storage" blade. The saved configuration is shown as a summary
 * (SQL Data, SQL Log, SQL tempdb). "Change configuration" opens the editor: the recommendation for the VM, and data, log and
 * tempdb storage, each with a drive location, disk type, size and number of disks. Ok saves, Discard drops the edits, and
 * "Reset to default recommendation" goes back to what Azure recommends.
 */
export function SqlStorageEditor({ product, regionId, value, onChange, idPrefix }: SqlStorageEditorProps) {
  const recommendation = useSqlStorageRecommendation(product, regionId)
  const panelId = useId()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<SqlStorageConfig | null>(null)
  const [expanded, setExpanded] = useState<Record<StorageSection, boolean>>({ data: true, log: false, tempDb: false })

  // Start from the recommendation, as Azure does: the user only edits what they want to change.
  useEffect(() => {
    if (value === null && recommendation.data) {
      const defaults = defaultSqlStorage(recommendation.data)
      if (defaults) onChange(defaults)
    }
  }, [value, recommendation.data, onChange])

  // The recommendation is per region, so an open editor for another region would be editing the wrong thing.
  useEffect(() => {
    setOpen(false)
  }, [regionId])

  const skus = recommendation.data?.skus ?? []

  if (recommendation.isLoading) {
    return (
      <section className={styles.storage} aria-label="SQL server storage">
        <h5 className={styles.readOnlyTitle}>Storage configuration</h5>
        <span className={styles.status} aria-live="polite">Loading the storage recommendation…</span>
      </section>
    )
  }
  if (recommendation.isError) {
    return (
      <section className={styles.storage} aria-label="SQL server storage">
        <h5 className={styles.readOnlyTitle}>Storage configuration</h5>
        <div role="alert" className={styles.alert}>
          <span>Could not load the storage recommendation.</span>
          <button type="button" className={styles.linkButton} onClick={() => void recommendation.refetch()}>
            Retry
          </button>
        </div>
      </section>
    )
  }

  const saved = value
  const problems = sqlStorageProblems(draft)
  const blocking = validateSqlStorage(draft)
  const setGroup = (section: StorageSection, group: SqlDiskGroup) =>
    setDraft((current) => {
      if (!current) return current
      return section === 'tempDb' ? { ...current, tempDb: { ...group, useLocalSsd: current.tempDb.useLocalSsd } } : { ...current, [section]: group }
    })

  const openEditor = () => {
    setDraft(saved)
    setOpen(true)
  }
  const ok = () => {
    if (draft && blocking.length === 0) {
      onChange(draft)
      setOpen(false)
    }
  }
  const reset = () => {
    const defaults = recommendation.data ? defaultSqlStorage(recommendation.data) : null
    if (defaults) setDraft(defaults)
  }

  const notices = draft ? storageNotices(draft) : []
  // While the editor is open it shows the notices for what is being edited, so the summary stays quiet.
  const savedWarnings = saved && !open ? storageNotices(saved).filter((notice) => notice.level === 'warning') : []

  return (
    <section className={styles.storage} aria-label="SQL server storage">
      <h5 className={styles.readOnlyTitle}>Storage configuration</h5>
      <p className={styles.note}>
        Customize performance, size, and workload type to optimize storage for this virtual machine. For optimal performance, separate drives will be created for data and log storage by default.
      </p>

      {saved && (
        <div className={styles.storageSummary} data-testid={`${idPrefix}-storage-summary`}>
          <dl className={styles.readOnlyList}>
            <dt>SQL Data</dt>
            <dd>{describeGroup(saved.data)}</dd>
            <dt>SQL Log</dt>
            <dd>{describeGroup(saved.log)}</dd>
            <dt>SQL tempdb</dt>
            <dd>{saved.tempDb.useLocalSsd ? 'Use local SSD drive' : describeGroup(saved.tempDb)}</dd>
          </dl>
          {savedWarnings.map((notice) => (
            <div key={notice.message} role="status" className={styles.warnBox}>
              <AlertCircle size={14} aria-hidden="true" />
              <span>{notice.message}</span>
            </div>
          ))}
          <div>
            <button type="button" className={styles.linkButton} aria-expanded={open} aria-controls={panelId} onClick={open ? () => setOpen(false) : openEditor}>
              Change configuration
            </button>
          </div>
        </div>
      )}

      {open && draft && (
        <div id={panelId} role="group" aria-label="Configure storage" className={styles.storagePanel}>
          <h5 className={styles.sectionTitle}>Configure storage</h5>
          <ReadOnlyInfoCard
            title="Storage configuration recommendation"
            items={[
              { label: 'VM size', value: draft.vm.vmSize, mono: true },
              { label: 'VM max IOPS', value: String(draft.vm.maxIops) },
              { label: 'VM max throughput', value: `${draft.vm.maxThroughputMbps} MBps` },
              { label: 'VM max data disks', value: String(draft.vm.maxDataDisks) },
              { label: 'Recommended disk type', value: draft.vm.recommendedDiskType },
            ]}
          />

          <Disclosure
            id={`${idPrefix}-data`}
            title={SECTION_TITLES.data}
            expanded={expanded.data}
            locked={sectionHasProblem(problems.data)}
            needsAttention={sectionHasProblem(problems.data)}
            onToggle={() => setExpanded((current) => ({ ...current, data: !current.data }))}
          >
            <p className={page.fieldHint}>{DESCRIPTIONS.data}</p>
            <DiskGroupFields idPrefix={`${idPrefix}-data`} title="SQL data" group={draft.data} skus={skus} maxDisks={draft.vm.maxDataDisks} problems={problems.data} locationLabel={LOCATION_LABELS.data} locationExample={LOCATION_EXAMPLES.data} onChange={(group) => setGroup('data', group)} />
            <label className={page.radioOption} style={{ alignSelf: 'flex-start' }}>
              <input
                type="checkbox"
                checked={draft.systemDatabasesOnDataDrive}
                onChange={(event) => setDraft({ ...draft, systemDatabasesOnDataDrive: event.target.checked })}
              />
              Set system databases (except tempdb) location to newly created data storage ({draft.data.driveLocation})
            </label>
          </Disclosure>

          <Disclosure
            id={`${idPrefix}-log`}
            title={SECTION_TITLES.log}
            expanded={expanded.log}
            locked={sectionHasProblem(problems.log)}
            needsAttention={sectionHasProblem(problems.log)}
            onToggle={() => setExpanded((current) => ({ ...current, log: !current.log }))}
          >
            <p className={page.fieldHint}>{DESCRIPTIONS.log}</p>
            <DiskGroupFields idPrefix={`${idPrefix}-log`} title="SQL log" group={draft.log} skus={skus} maxDisks={draft.vm.maxDataDisks} problems={problems.log} locationLabel={LOCATION_LABELS.log} locationExample={LOCATION_EXAMPLES.log} onChange={(group) => setGroup('log', group)} />
          </Disclosure>

          <Disclosure
            id={`${idPrefix}-tempdb`}
            title={SECTION_TITLES.tempDb}
            expanded={expanded.tempDb}
            locked={sectionHasProblem(problems.tempDb)}
            needsAttention={sectionHasProblem(problems.tempDb)}
            onToggle={() => setExpanded((current) => ({ ...current, tempDb: !current.tempDb }))}
          >
            <label className={page.radioOption} style={{ alignSelf: 'flex-start' }}>
              <input
                type="checkbox"
                checked={draft.tempDb.useLocalSsd}
                onChange={(event) => setDraft({ ...draft, tempDb: { ...draft.tempDb, useLocalSsd: event.target.checked } })}
              />
              Use local SSD drive
            </label>
            {draft.tempDb.useLocalSsd ? (
              <p className={page.fieldHint}>tempdb is kept on the VM’s temporary disk, which is the fastest option. Its contents are recreated whenever the VM starts.</p>
            ) : (
              <>
                <p className={page.fieldHint}>{DESCRIPTIONS.tempDb}</p>
                <DiskGroupFields idPrefix={`${idPrefix}-tempdb`} title="SQL tempdb" group={draft.tempDb} skus={skus} maxDisks={draft.vm.maxDataDisks} problems={problems.tempDb} locationLabel={LOCATION_LABELS.tempDb} locationExample={LOCATION_EXAMPLES.tempDb} onChange={(group) => setGroup('tempDb', group)} />
              </>
            )}
          </Disclosure>

          {problems.general.length > 0 && (
            <ul role="alert" className={styles.diskProblems}>
              {problems.general.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
          {notices.map((notice) => (
            <div key={notice.message} role="status" className={notice.level === 'warning' ? styles.warnBox : styles.infoBox}>
              {notice.level === 'warning' ? <AlertCircle size={14} aria-hidden="true" /> : <Info size={14} aria-hidden="true" />}
              <span>{notice.message}</span>
            </div>
          ))}

          <div className={styles.storageButtons}>
            <Button type="button" disabled={blocking.length > 0} onClick={ok}>
              Ok
            </Button>
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
              Discard
            </Button>
            <Button type="button" variant="secondary" onClick={reset}>
              Reset to default recommendation
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
